import React, { useState, useEffect, useCallback } from 'react';
import TopNav from '../components/TopNav';
import SideIcons from '../components/SideIcons';
import { PLATFORM_ICONS } from '../components/SideIcons/SideIcons';
import ComposeModal from '../components/ComposeModal';
import Footer from '../components/Footer';
import PostMetrics from '../components/PostMetrics/PostMetrics';
import PostTypeIndicator from '../components/PostTypeIndicator/PostTypeIndicator';
import RawResponseModal from '../components/RawResponseModal/RawResponseModal';
import { postsAPI } from '../api';
import { usePostActions } from '../hooks/usePostActions';
import { getPostPlatforms, hasRawResponse, getStatusLabel, formatPostDate, EDITABLE_STATUSES, PUBLISHABLE_STATUSES } from '../utils/postUtils';
import './Posts.css';

const Posts = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Status filter state
  const [statusFilter, setStatusFilter] = useState('');

  // Active platforms from SideIcons
  const [activePlatforms, setActivePlatforms] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: perPage,
        sort_by: startDate || endDate ? 'date_range' : 'recent',
      };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const data = await postsAPI.getPaginated(params);
      setPosts(data.posts || []);
      setTotalCount(data.pagination?.total_count || 0);
      setTotalPages(data.pagination?.total_pages || 1);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, perPage, startDate, endDate]);

  // Shared post actions hook
  const {
    isComposeModalOpen,
    editingPost,
    editingPostForModal,
    publishingPostId,
    deletingPostId,
    showRawResponse,
    viewingRawResponsePost,
    handleComposeClick,
    handleCloseModal,
    handleSubmitPost,
    openEditForPost,
    handlePublishNow,
    handleDeletePost,
    handleViewRawResponse,
    setShowRawResponse,
  } = usePostActions(fetchPosts);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Apply client-side filters
  const filteredPosts = (posts || []).filter(post => {
    if (statusFilter && post.status !== statusFilter) return false;
    if (activePlatforms && activePlatforms.length > 0) {
      const postPlatforms = getPostPlatforms(post);
      const hasMatch = postPlatforms.some(p => activePlatforms.includes(p));
      if (!hasMatch) return false;
    }
    return true;
  });

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        buttons.push(i);
      }
    } else {
      buttons.push(1);
      if (currentPage > 3) buttons.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);

      for (let i = start; i <= end; i++) {
        buttons.push(i);
      }

      if (currentPage < totalPages - 2) buttons.push('...');
      buttons.push(totalPages);
    }

    return buttons.map((page, idx) => {
      if (page === '...') {
        return <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>;
      }
      return (
        <button
          key={page}
          className={`pagination-button ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageChange(page)}
          disabled={currentPage === page}
        >
          {page}
        </button>
      );
    });
  };

  return (
    <div className="page-wrapper">
      <TopNav activePage="posts" onComposeClick={handleComposeClick} />
      <SideIcons
        onPlatformToggle={setActivePlatforms}
        activePlatforms={activePlatforms}
        requireCapability="can_publish"
      />

      <main className="content posts-content">
        {/* Header */}
        <div className="posts-header">
          <h1>Posts</h1>
          <div className="posts-count">
            {totalCount} total post{totalCount !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Filter Bar */}
        <section className="filter-bar">
          <div className="filter-item">
            <label>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
              className="filter-input"
            />
          </div>
          <div className="filter-item">
            <label>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
              className="filter-input"
            />
          </div>
          <div className="filter-item">
            <label>Status</label>
            <select value={statusFilter} onChange={handleFilterChange}>
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="published">Published</option>
              <option value="publish_pending">Publish Pending</option>
              <option value="publish_failed">Publish Failed</option>
              <option value="platform-existing">Platform Existing</option>
            </select>
          </div>
          <div className="filter-item">
            <label>Per Page</label>
            <select value={perPage} onChange={handlePerPageChange}>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <button className="clear" onClick={clearAllFilters}>Clear Filters</button>
          <button className="sync-btn" onClick={fetchPosts} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </section>

        {/* Posts List */}
        {loading ? (
          <div className="posts-loading">Loading posts...</div>
        ) : filteredPosts.length === 0 ? (
          <div className="posts-empty">
            <p>No posts found</p>
            <p className="posts-empty-hint">Try adjusting your filters or compose a new post</p>
          </div>
        ) : (
          <>
            <div className="posts-list">
              {filteredPosts.map(post => {
                const postPlatforms = getPostPlatforms(post);
                const status = post.status;

                return (
                  <div
                    key={post.id}
                    className={`post-item-list ${status}`}
                    title={post.title || post.content?.substring(0, 50)}
                    onClick={() => openEditForPost(post)}
                  >
                    {/* Date Column */}
                    <div className="post-list-date">
                      {formatPostDate(post.scheduled_at || post.created_at)}
                    </div>

                    {/* Platform Icons */}
                    <div className="post-list-platforms">
                      <div className="post-platform-icons">
                        {postPlatforms.map((p) => (
                          <img
                            key={p}
                            className="post-platform-icon"
                            src={PLATFORM_ICONS[p]?.src || ''}
                            alt={p}
                            title={p}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="post-list-content">
                      {post.title && <div className="post-list-title">{post.title}</div>}
                      <div className="post-list-text">{post.content}</div>
                      {post.scheduled_at && (
                        <div className="post-list-scheduled">
                          Scheduled: {new Date(post.scheduled_at).toLocaleString()}
                        </div>
                      )}
                    </div>

                    {/* Status Badge */}
                    <div className="post-list-status">
                      <span className={`status-badge ${status}`}>
                        {getStatusLabel(status)}
                      </span>
                    </div>

                    {/* Metrics */}
                    {status === 'published' && post.metrics && (
                      <div className="post-list-metrics">
                        <PostMetrics metrics={post.metrics} platform={postPlatforms[0]} />
                      </div>
                    )}

                    {/* Post Type Indicator */}
                    <PostTypeIndicator post={post} platform={postPlatforms[0]} />

                    {/* Action Buttons */}
                    <div className="post-list-actions" onClick={(e) => e.stopPropagation()}>
                      {/* EDIT */}
                      {EDITABLE_STATUSES.includes(status) && (
                        <button
                          type="button"
                          className="post-action-btn"
                          aria-label="Edit post"
                          title="Edit"
                          onClick={() => openEditForPost(post)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 20H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      {/* PUBLISH */}
                      {PUBLISHABLE_STATUSES.includes(status) && (
                        <button
                          type="button"
                          className="post-action-btn"
                          aria-label="Publish post now"
                          title={publishingPostId === post?.id ? 'Publishing...' : 'Publish now'}
                          onClick={() => handlePublishNow(post)}
                          disabled={!post || publishingPostId === post?.id}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M12 3v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M7 8l5-5 5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M5 21h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                        </button>
                      )}
                      {/* RAW RESPONSE */}
                      {hasRawResponse(post) && (
                        <button
                          type="button"
                          className="post-action-btn"
                          aria-label="View raw response"
                          title="View raw response"
                          onClick={() => handleViewRawResponse(post)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2"/>
                            <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2"/>
                          </svg>
                        </button>
                      )}
                      {/* DELETE */}
                      <button
                        type="button"
                        className="post-action-btn"
                        aria-label="Delete post"
                        title="Delete"
                        disabled={deletingPostId === post.id}
                        onClick={() => handleDeletePost(post.id, posts)}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                          <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls">
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <div className="pagination-buttons">
                <button
                  className="pagination-nav-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  ‹
                </button>
                {renderPaginationButtons()}
                <button
                  className="pagination-nav-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  ›
                </button>
              </div>
            </div>
          </>
        )}
      </main>

      <ComposeModal
        isOpen={isComposeModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitPost}
        onDelete={fetchPosts}
        editPost={editingPost}
        canViewPlatforms={!!(editingPostForModal && editingPostForModal.platform_statuses && Object.keys(editingPostForModal.platform_statuses || {}).length > 0)}
      />

      <RawResponseModal
        isOpen={showRawResponse}
        onClose={() => setShowRawResponse(false)}
        rawResponse={viewingRawResponsePost}
      />

      <Footer />
    </div>
  );
};

export default Posts;
